import { useCallback, useEffect, useState } from "react";
import { Wifi, WifiOff, RefreshCw } from "lucide-react";
import api from "../lib/api";

function ApRow({ ap }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5" data-testid={`wifi-ap-${ap.name}`}>
      {ap.quiet ? <WifiOff className="h-3.5 w-3.5 text-amber-600" /> : <Wifi className="h-3.5 w-3.5 text-emerald-600" />}
      <p className="text-sm text-brand-ink flex-1 truncate">{ap.name}</p>
      {ap.quiet
        ? <span className="text-[11px] text-amber-700">No devices now · last active {new Date(ap.last_seen).toLocaleDateString()}</span>
        : <span className="text-[11px] text-brand-muted">{ap.devices} device{ap.devices === 1 ? "" : "s"}{ap.companies.length ? ` · ${ap.companies.length} compan${ap.companies.length === 1 ? "y" : "ies"}` : ""}{ap.signal != null ? ` · ${ap.signal} dBm` : ""}</span>}
    </div>
  );
}

export function WifiPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData((await api.get("/members/wifi/activity")).data); }
    catch (e) { setError(e.userMessage); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="space-y-4" data-testid="wifi-panel">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <h2 className="font-serif text-2xl text-brand-ink">WiFi</h2>
          <p className="text-xs text-brand-muted mt-1">Access points with devices on “{data?.ssid || "Bond Collective"}” right now, from Yardi. Amber means an access point was busy this week but has no devices now — check it.</p>
        </div>
        <button onClick={load} disabled={loading} data-testid="wifi-refresh"
          className="inline-flex items-center gap-1.5 border border-brand-border rounded-full px-4 py-1.5 text-sm text-brand-ink hover:bg-brand-hover transition-colors disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {error && <p className="text-sm text-brand-neg" data-testid="wifi-error">WiFi activity couldn't load: {error}</p>}
      {loading && !data && <div className="h-24 bg-brand-hover animate-pulse rounded-lg" />}
      {data?.no_data && (
        <p className="text-sm text-brand-muted bg-brand-surface border border-brand-border rounded-lg px-4 py-3" data-testid="wifi-no-data">
          Yardi isn't reporting any devices on “{data.ssid}” at any location right now. Usually that means Yardi isn't receiving device data from your WiFi equipment — ask your Yardi Kube rep to turn on the WiFi data feed. Passcode lookup and reset in Member Lookup work either way.
        </p>
      )}
      {data && !data.no_data && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.locations.map((l) => (
            <div key={l.location} className="bg-brand-surface border border-brand-border rounded-lg" data-testid={`wifi-loc-${l.location}`}>
              <div className="flex items-center px-4 py-3 border-b border-brand-border">
                <p className="text-sm font-medium text-brand-ink flex-1">{l.location}</p>
                <span className="text-[11px] text-brand-tertiary">{l.devices} devices</span>
              </div>
              {l.error ? <p className="px-4 py-3 text-xs text-brand-neg">{l.error}</p>
                : l.aps.length ? <div className="divide-y divide-brand-border">{l.aps.map((ap) => <ApRow key={ap.name} ap={ap} />)}</div>
                : <p className="px-4 py-3 text-xs text-brand-tertiary">No devices connected right now.</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
